import { useTranslation } from 'react-i18next'

import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui'
import { defaultLanguage, isSupportedLanguage, type SupportedLanguage } from '@/lib/i18n'
import { cn } from '@/lib/utils'

interface LanguageOption {
  readonly value: SupportedLanguage
  readonly labelKey: 'settings.language.options.ptBR' | 'settings.language.options.enUS'
}

const LANGUAGE_OPTIONS: readonly LanguageOption[] = [
  { value: 'pt-BR', labelKey: 'settings.language.options.ptBR' },
  { value: 'en-US', labelKey: 'settings.language.options.enUS' },
]

/**
 * Card de idioma da interface: troca o idioma na hora (i18n.changeLanguage) e persiste a escolha
 * (localStorage, via listener em lib/i18n). O idioma atual fica sempre marcado. Acessível como um
 * radiogroup; a tela reflete a troca imediatamente porque `useTranslation` re-renderiza no evento
 * `languageChanged`.
 */
export function LanguageCard() {
  const { t, i18n } = useTranslation()

  const current: SupportedLanguage = isSupportedLanguage(i18n.language)
    ? i18n.language
    : defaultLanguage

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.language.title')}</CardTitle>
        <CardDescription>{t('settings.language.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div
          role="radiogroup"
          aria-label={t('settings.language.groupLabel')}
          className="flex flex-col gap-2 sm:flex-row"
        >
          {LANGUAGE_OPTIONS.map((option) => {
            const selected = option.value === current
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                lang={option.value}
                onClick={() => {
                  if (!selected) {
                    void i18n.changeLanguage(option.value)
                  }
                }}
                className={cn(
                  'flex flex-1 items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
                  selected
                    ? 'border-action-primary bg-action-primary/5'
                    : 'border-border-default hover:border-border-strong',
                )}
              >
                <span className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                      selected ? 'border-action-primary' : 'border-border-strong',
                    )}
                  >
                    {selected ? <span className="h-2 w-2 rounded-full bg-action-primary" /> : null}
                  </span>
                  <span className="text-sm font-medium text-foreground-primary">
                    {t(option.labelKey)}
                  </span>
                </span>
                {selected ? <Badge variant="info">{t('settings.language.current')}</Badge> : null}
              </button>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
